/**
 * EADiffViewer Component
 * Side-by-side diff view for code changes
 *
 * Current Implementation (Phase 5):
 * - Lightweight side-by-side text comparison
 * - Line numbers with synchronized scrolling
 * - Line-by-line diff highlighting (added/removed/changed)
 * - MQL5/C-like code formatting
 *
 * Future Enhancement (Phase 6):
 * - Monaco Editor integration for full VSCode experience
 * - Install: npm install @monaco-editor/react
 * - Provides: syntax highlighting, inline diffs, minimap
 *
 * Features:
 * - Original (left) vs Suggested (right) view
 * - Save button with _QAI suffix reminder
 * - Back to results navigation
 * - Line-by-line diff highlighting
 */

import React, { useMemo, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CodeIcon from '@mui/icons-material/Code';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { getFileTypeConfig, generateQAIFileName } from '../../../api/qbridgeEaApi';

// ═══════════════════════════════════════════════════════════════════════════
// FUTURE: Monaco Editor Integration (Phase 6)
// When ready to add Monaco, install: npm install @monaco-editor/react
// Then uncomment: import { DiffEditor } from '@monaco-editor/react';
// And set USE_MONACO = true
// ═══════════════════════════════════════════════════════════════════════════
const USE_MONACO = false; // Toggle for Monaco integration

/**
 * Simple line-by-line diff calculation
 * Returns array of { type: 'same' | 'added' | 'removed' | 'changed', originalLine, modifiedLine }
 */
const computeLineDiff = (original, modified) => {
  const originalLines = original.split('\n');
  const modifiedLines = modified.split('\n');
  const maxLines = Math.max(originalLines.length, modifiedLines.length);
  const diff = [];

  for (let i = 0; i < maxLines; i++) {
    const origLine = originalLines[i];
    const modLine = modifiedLines[i];

    if (origLine === undefined) {
      diff.push({ type: 'added', originalLine: '', modifiedLine: modLine, lineNum: i + 1 });
    } else if (modLine === undefined) {
      diff.push({ type: 'removed', originalLine: origLine, modifiedLine: '', lineNum: i + 1 });
    } else if (origLine === modLine) {
      diff.push({ type: 'same', originalLine: origLine, modifiedLine: modLine, lineNum: i + 1 });
    } else {
      diff.push({ type: 'changed', originalLine: origLine, modifiedLine: modLine, lineNum: i + 1 });
    }
  }

  return diff;
};

/**
 * Code Line Component - Renders a single line with styling
 */
const CodeLine = ({ lineNum, content, type, side }) => {
  const getBgColor = () => {
    if (type === 'same') return 'transparent';
    if (type === 'added' && side === 'right') return 'rgba(39, 181, 109, 0.15)';
    if (type === 'removed' && side === 'left') return 'rgba(239, 68, 68, 0.15)';
    if (type === 'changed') {
      return side === 'left' ? 'rgba(251, 191, 36, 0.1)' : 'rgba(39, 181, 109, 0.1)';
    }
    return 'transparent';
  };

  const getLineNumColor = () => {
    if (type === 'same') return 'rgba(255, 255, 255, 0.3)';
    if (type === 'added') return '#27B56D';
    if (type === 'removed') return '#EF4444';
    if (type === 'changed') return '#FBBF24';
    return 'rgba(255, 255, 255, 0.3)';
  };

  const getIndicator = () => {
    if (type === 'added' && side === 'right') return '+';
    if (type === 'removed' && side === 'left') return '-';
    if (type === 'changed') return '~';
    return ' ';
  };

  const getIndicatorColor = () => {
    if (type === 'added') return '#27B56D';
    if (type === 'removed') return '#EF4444';
    if (type === 'changed') return '#FBBF24';
    return 'transparent';
  };

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '22px',
        backgroundColor: getBgColor(),
        '&:hover': {
          backgroundColor: type === 'same'
            ? 'rgba(255, 255, 255, 0.02)'
            : getBgColor()
        }
      }}
    >
      {/* Line Number */}
      <Box
        sx={{
          width: '48px',
          minWidth: '48px',
          paddingRight: '8px',
          textAlign: 'right',
          color: getLineNumColor(),
          fontSize: '11px',
          fontFamily: "'JetBrains Mono', monospace",
          userSelect: 'none',
          borderRight: '1px solid rgba(255, 255, 255, 0.06)'
        }}
      >
        {lineNum}
      </Box>

      {/* Change Indicator */}
      <Box
        sx={{
          width: '20px',
          minWidth: '20px',
          textAlign: 'center',
          color: getIndicatorColor(),
          fontSize: '12px',
          fontFamily: "'JetBrains Mono', monospace",
          fontWeight: 700
        }}
      >
        {getIndicator()}
      </Box>

      {/* Code Content */}
      <Box
        sx={{
          flex: 1,
          paddingLeft: '8px',
          paddingRight: '16px',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}
      >
        <Typography
          component="span"
          sx={{
            fontFamily: "'JetBrains Mono', 'Fira Code', Consolas, monospace",
            fontSize: '12px',
            lineHeight: '22px',
            color: type === 'same' ? 'rgba(255, 255, 255, 0.75)' : '#FFFFFF',
            whiteSpace: 'pre',
            display: 'block'
          }}
        >
          {content || ' '}
        </Typography>
      </Box>
    </Box>
  );
};

/**
 * EADiffViewer Component
 */
const EADiffViewer = ({
  file = null,
  originalSource = '',
  modifiedSource = '',
  onBack,
  onSave,
  isSaving = false,
  saveResult = null,
  saveError = null
}) => {
  const leftPanelRef = useRef(null);
  const rightPanelRef = useRef(null);

  // Compute line-by-line diff
  const diffLines = useMemo(() => {
    return computeLineDiff(originalSource, modifiedSource);
  }, [originalSource, modifiedSource]);

  // Count changes for summary
  const changeStats = useMemo(() => {
    const stats = { added: 0, removed: 0, changed: 0 };
    diffLines.forEach(line => {
      if (line.type === 'added') stats.added++;
      else if (line.type === 'removed') stats.removed++;
      else if (line.type === 'changed') stats.changed++;
    });
    return stats;
  }, [diffLines]);

  // Synchronized scrolling
  const handleScroll = useCallback((e, source) => {
    const target = source === 'left' ? rightPanelRef.current : leftPanelRef.current;
    if (target) {
      target.scrollTop = e.target.scrollTop;
    }
  }, []);

  if (!file) return null;

  const fileConfig = getFileTypeConfig(file.file_type);
  const qaiFileName = generateQAIFileName(file.name);

  const handleSave = () => {
    if (onSave) {
      onSave(modifiedSource);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundColor: '#0F0F14',
        borderRadius: '12px',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        overflow: 'hidden'
      }}
    >
      {/* ════════════════════════════════════════════════════════════════════════
          HEADER
          ════════════════════════════════════════════════════════════════════════ */}
      <Box
        sx={{
          padding: '12px 16px',
          borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
          backgroundColor: 'rgba(255, 255, 255, 0.02)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {/* Back Button */}
          <Tooltip title="Back to Results">
            <IconButton
              onClick={onBack}
              size="small"
              sx={{
                color: 'rgba(255, 255, 255, 0.6)',
                '&:hover': {
                  color: '#FFFFFF',
                  backgroundColor: 'rgba(255, 255, 255, 0.08)'
                }
              }}
            >
              <ArrowBackIcon sx={{ fontSize: '1.25rem' }} />
            </IconButton>
          </Tooltip>

          <CodeIcon sx={{ color: '#7c3aed', fontSize: '1.125rem' }} />
          <Typography
            sx={{
              color: '#FFFFFF',
              fontSize: '0.9375rem',
              fontWeight: 600,
              fontFamily: "'Inter', -apple-system, sans-serif"
            }}
          >
            Code Changes
          </Typography>

          {/* File Badge */}
          <Box
            sx={{
              backgroundColor: `${fileConfig.color}20`,
              color: fileConfig.color,
              fontWeight: 700,
              fontSize: '0.625rem',
              padding: '3px 6px',
              borderRadius: '4px',
              border: `1px solid ${fileConfig.color}40`
            }}
          >
            {fileConfig.label}
          </Box>

          {/* Change Stats */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, ml: 1 }}>
            {changeStats.added > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                <AddIcon sx={{ fontSize: '0.875rem', color: '#27B56D' }} />
                <Typography sx={{ color: '#27B56D', fontSize: '0.75rem', fontWeight: 600 }}>
                  {changeStats.added}
                </Typography>
              </Box>
            )}
            {changeStats.removed > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                <RemoveIcon sx={{ fontSize: '0.875rem', color: '#EF4444' }} />
                <Typography sx={{ color: '#EF4444', fontSize: '0.75rem', fontWeight: 600 }}>
                  {changeStats.removed}
                </Typography>
              </Box>
            )}
            {changeStats.changed > 0 && (
              <Typography sx={{ color: '#FBBF24', fontSize: '0.75rem', fontWeight: 600 }}>
                ~{changeStats.changed}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Save Button */}
        {!saveResult && (
          <Button
            onClick={handleSave}
            disabled={isSaving}
            startIcon={isSaving ? <CircularProgress size={16} /> : <SaveIcon />}
            sx={{
              background: 'linear-gradient(135deg, #27B56D 0%, #22C55E 100%)',
              color: '#FFFFFF',
              textTransform: 'none',
              fontWeight: 600,
              padding: '8px 16px',
              borderRadius: '8px',
              fontSize: '0.8125rem',
              '&:hover': {
                background: 'linear-gradient(135deg, #22A55E 0%, #16A34A 100%)'
              },
              '&.Mui-disabled': {
                background: 'rgba(39, 181, 109, 0.3)',
                color: 'rgba(255, 255, 255, 0.5)'
              }
            }}
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        )}
      </Box>

      {/* Column Labels */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          borderBottom: '1px solid rgba(255, 255, 255, 0.08)'
        }}
      >
        <Box
          sx={{
            padding: '8px 16px',
            backgroundColor: 'rgba(239, 68, 68, 0.05)',
            borderRight: '1px solid rgba(255, 255, 255, 0.08)',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          <Typography
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              fontSize: '0.75rem',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}
          >
            Original
          </Typography>
          <Typography
            sx={{
              color: 'rgba(255, 255, 255, 0.4)',
              fontSize: '0.6875rem',
              fontFamily: "'JetBrains Mono', monospace"
            }}
          >
            {file.name}
          </Typography>
        </Box>
        <Box
          sx={{
            padding: '8px 16px',
            backgroundColor: 'rgba(39, 181, 109, 0.05)',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          <Typography
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              fontSize: '0.75rem',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}
          >
            Q.AI Suggested
          </Typography>
          <Typography
            sx={{
              color: 'rgba(39, 181, 109, 0.7)',
              fontSize: '0.6875rem',
              fontFamily: "'JetBrains Mono', monospace"
            }}
          >
            {qaiFileName}
          </Typography>
        </Box>
      </Box>

      {/* ════════════════════════════════════════════════════════════════════════
          DIFF CONTENT - Side by Side with Synchronized Scroll
          ════════════════════════════════════════════════════════════════════════ */}
      <Box
        sx={{
          flex: 1,
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          overflow: 'hidden'
        }}
      >
        {/* Left Panel - Original */}
        <Box
          ref={leftPanelRef}
          onScroll={(e) => handleScroll(e, 'left')}
          sx={{
            overflow: 'auto',
            backgroundColor: 'rgba(239, 68, 68, 0.02)',
            borderRight: '1px solid rgba(255, 255, 255, 0.08)',
            '&::-webkit-scrollbar': { width: '8px', height: '8px' },
            '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '4px',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.2)' }
            }
          }}
        >
          {diffLines.map((line, idx) => (
            <CodeLine
              key={`left-${idx}`}
              lineNum={line.lineNum}
              content={line.originalLine}
              type={line.type}
              side="left"
            />
          ))}
        </Box>

        {/* Right Panel - Modified */}
        <Box
          ref={rightPanelRef}
          onScroll={(e) => handleScroll(e, 'right')}
          sx={{
            overflow: 'auto',
            backgroundColor: 'rgba(39, 181, 109, 0.02)',
            '&::-webkit-scrollbar': { width: '8px', height: '8px' },
            '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '4px',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.2)' }
            }
          }}
        >
          {diffLines.map((line, idx) => (
            <CodeLine
              key={`right-${idx}`}
              lineNum={line.lineNum}
              content={line.modifiedLine}
              type={line.type}
              side="right"
            />
          ))}
        </Box>
      </Box>

      {/* ════════════════════════════════════════════════════════════════════════
          SAVE RESULT / ERROR MESSAGES
          ════════════════════════════════════════════════════════════════════════ */}
      {saveError && (
        <Box sx={{ padding: '12px 16px', borderTop: '1px solid rgba(255, 255, 255, 0.08)' }}>
          <Alert
            severity="error"
            sx={{
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              color: '#EF4444',
              border: '1px solid rgba(239, 68, 68, 0.2)',
              '& .MuiAlert-icon': { color: '#EF4444' }
            }}
          >
            {saveError}
          </Alert>
        </Box>
      )}

      {saveResult && (
        <Box
          sx={{
            padding: '16px 20px',
            borderTop: '1px solid rgba(255, 255, 255, 0.08)',
            backgroundColor: 'rgba(39, 181, 109, 0.08)'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
            <CheckCircleIcon sx={{ color: '#27B56D', fontSize: '1.5rem', mt: 0.25 }} />
            <Box sx={{ flex: 1 }}>
              <Typography sx={{ color: '#27B56D', fontSize: '1rem', fontWeight: 600, mb: 0.5 }}>
                Saved Successfully!
              </Typography>
              <Typography sx={{ color: '#FFFFFF', fontSize: '0.875rem', mb: 0.5 }}>
                New file: <strong style={{ fontFamily: "'JetBrains Mono', monospace" }}>{saveResult.saved_as || qaiFileName}</strong>
              </Typography>
              <Typography sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.8125rem' }}>
                Location: MQL5/{file.file_type === 'expert' ? 'Experts' : file.file_type === 'indicator' ? 'Indicators' : 'Scripts'}/
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  mt: 1.5,
                  padding: '10px 14px',
                  backgroundColor: 'rgba(39, 181, 109, 0.1)',
                  borderRadius: '8px',
                  border: '1px solid rgba(39, 181, 109, 0.25)'
                }}
              >
                <InfoOutlinedIcon sx={{ color: '#27B56D', fontSize: '1rem' }} />
                <Typography sx={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.8125rem' }}>
                  Your original file <strong>{file.name}</strong> is untouched.
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      {/* Footer Info */}
      {!saveResult && !saveError && (
        <Box
          sx={{
            padding: '12px 16px',
            borderTop: '1px solid rgba(255, 255, 255, 0.08)',
            backgroundColor: 'rgba(255, 255, 255, 0.02)',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          <InfoOutlinedIcon sx={{ color: '#71717A', fontSize: '1rem' }} />
          <Typography sx={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.75rem' }}>
            Saving will create <strong style={{ color: 'rgba(255, 255, 255, 0.8)' }}>{qaiFileName}</strong>. Your original file will not be modified.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default EADiffViewer;
